import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
} from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { postTemplates } from "../../../MasterData/postTemplate";

const TemplateModal = ({
  open,
  onClose,
  onSelect,
  searchQuery,
  onSearchChange,
  selectedTemplate,
  onClearSelection,
}) => {
  // Filter templates based on search query
  const filteredTemplates = postTemplates.filter(
    (template) =>
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#4B5AB6",
          color: "white",
          fontWeight: 600,
          fontSize: "1.25rem",
          textAlign: "center",
          py: 2,
        }}
      >
        📝 Choose Post Template
      </DialogTitle>

      <Box sx={{ p: 3, bgcolor: "#f8fafc" }}>
        <TextField
          fullWidth
          placeholder="🔍 Search templates by name, title, or content..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "& fieldset": {
                borderColor: "#e2e8f0",
              },
              "&:hover fieldset": {
                borderColor: "#4B5AB6",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#4B5AB6",
              },
            },
          }}
          InputProps={{
            sx: { fontSize: "0.95rem" },
          }}
        />
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: "#f8fafc" }}>
        <Box sx={{ px: 3, pb: 3 }}>
          {/* Template Count */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#64748b", fontWeight: 500 }}
            >
              {filteredTemplates.length} template
              {filteredTemplates.length !== 1 ? "s" : ""} found
            </Typography>
            {searchQuery && (
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Searching for: "{searchQuery}"
              </Typography>
            )}
          </Box>

          {filteredTemplates.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "white",
                borderRadius: 3,
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography variant="h6" sx={{ color: "#64748b", mb: 1 }}>
                {searchQuery
                  ? "🔍 No templates found"
                  : "📋 No templates available"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                {searchQuery
                  ? "Try adjusting your search terms or browse all templates"
                  : "Templates will appear here when available"}
              </Typography>
              {searchQuery && (
                <Button
                  onClick={() => onSearchChange("")}
                  sx={{ mt: 2, color: "#4B5AB6" }}
                >
                  Clear Search
                </Button>
              )}
            </Paper>
          ) : (
            <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>
              <Grid container spacing={2}>
                {filteredTemplates.map((template, index) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Paper
                      sx={{
                        p: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        border:
                          selectedTemplate?.id === template.id
                            ? "2px solid #4B5AB6"
                            : "1px solid #e2e8f0",
                        borderRadius: 3,
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        bgcolor:
                          selectedTemplate?.id === template.id
                            ? "#f0f4ff"
                            : "white",
                        "&:hover": {
                          borderColor: "#4B5AB6",
                          boxShadow: "0 4px 12px rgba(75,90,182,0.15)",
                          transform: "translateY(-2px)",
                        },
                      }}
                      onClick={() => onSelect(template)}
                    >
                      {/* Template Header */}
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#1e293b",
                              fontSize: "1rem",
                              lineHeight: 1.2,
                              flex: 1,
                              mr: 1,
                            }}
                          >
                            {template.name}
                          </Typography>
                          {selectedTemplate?.id === template.id && (
                            <Box
                              sx={{
                                bgcolor: "#4B5AB6",
                                color: "white",
                                borderRadius: "50%",
                                width: 20,
                                height: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                              }}
                            >
                              ✓
                            </Box>
                          )}
                        </Box>

                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#4B5AB6",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            mb: 1,
                          }}
                        >
                          {template.title}
                        </Typography>

                        {template.hashtags && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#059669",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              fontStyle: "italic",
                              display: "block",
                            }}
                          >
                            {template.hashtags}
                          </Typography>
                        )}
                      </Box>

                      {/* Template Content Preview */}
                      <Box sx={{ flexGrow: 1, mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#475569",
                            lineHeight: 1.5,
                            fontSize: "0.8rem",
                            display: "-webkit-box",
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: "4.5rem",
                          }}
                        >
                          {template.content}
                        </Typography>
                      </Box>

                      {/* Template Footer */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          pt: 2,
                          borderTop: "1px solid #e2e8f0",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Chip
                            label="Template"
                            size="small"
                            sx={{
                              bgcolor: "#e0e7ff",
                              color: "#4B5AB6",
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              height: 20,
                            }}
                          />
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{ color: "#94a3b8", fontSize: "0.7rem" }}
                        >
                          #{index + 1}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          gap: 2,
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: "#64748b",
            fontWeight: 600,
            px: 3,
          }}
        >
          Cancel
        </Button>

        <Box sx={{ display: "flex", gap: 2 }}>
          {selectedTemplate && (
            <Button
              onClick={onClearSelection}
              variant="outlined"
              sx={{
                color: "#64748b",
                borderColor: "#e2e8f0",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#cbd5e1",
                  bgcolor: "#f1f5f9",
                },
              }}
            >
              Clear Selection
            </Button>
          )}

          <Button
            variant="contained"
            onClick={() => onSelect(selectedTemplate)}
            disabled={!selectedTemplate}
            startIcon={selectedTemplate ? <CheckCircleIcon /> : null}
            sx={{
              bgcolor: "#4B5AB6",
              fontWeight: 600,
              px: 4,
              py: 1,
              "&:hover": { bgcolor: "#3a4891" },
              "&:disabled": {
                bgcolor: "#cbd5e1",
                color: "#94a3b8",
              },
            }}
          >
            {selectedTemplate ? "Use This Template" : "Select a Template"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateModal;
